/**
 * Manage Event Calendar Page Component
 * Displays event data with pagination, loading/error states
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useNavigate} from 'react-router-dom';

import {DataTable, Cell, TableHeader, Indent} from '@pb/kit-component-data-table';
import type {Column, RowData, CellOptions, RenderHeaders, RenderRows} from '@pb/kit-component-data-table';
import {Loading} from '@pb/kit-component-loading';
import {PaginationChangeHandler, PaginationStripe} from '@pb/kit-component-pagination';
import {H2} from '@pb/kit-component-typography';

import {useAppDispatch} from '/src/store/hooks';
import {ROOT} from '/src/middleware/api';
import {updateEventField, resetEventForm} from '/src/modules/event-details/services/reducer';
import {getEventById} from './services/eventsApi';
import {
    fetchEventTypesRequest,
    fetchEventTypesSuccess,
    fetchEventTypesFailure,
    fetchStatusesRequest,
    fetchStatusesSuccess,
    fetchStatusesFailure,
} from '/src/openapi/ccdb-bff/apis/EventDetailsApi';
import type {EventDetail} from '/src/openapi/ccdb-bff/models';

import {EventData} from './services/eventsApi';
import {DEFAULT_PAGE_SIZE, PAGINATION_SIZES, useEvents, ToggleFeedback} from './useEvents';
import SearchFilters, {SearchFiltersState} from './SearchFilters';
import StatusPopup from './StatusPopup';

import {
    eventCalendarContainer,
    filtersPanel,
    dataPanel,
    errorContainer,
    emptyContainer,
    loadingContainer,
    tableContainer,
    tableWrapper,
    headerSection,
    headerTitle,
    headerButton,
    statusLink,
    modalOverlay,
    modalContainer,
    modalHeader,
    modalTitle,
    modalBody,
    modalTextSuccess,
    modalTextError,
    modalTextPartial,
    modalFooter,
    modalButtonConfirm,
    modalButtonCancel,
    checkboxCell,
    cellContent,
    cellContentWrap,
    toggleOverlay,
    toggleOverlayText,
    toggleSpinner,
} from './manage-event-calendar.styles.module.styl';


/** Column definitions for the event calendar data table */
const TABLE_COLUMNS: Column[] = [
    {columnId: 'select',       fixed: true,  resizable: false, defaultWidth: 40},
    {columnId: 'eventId',      fixed: true,  resizable: false, defaultWidth: 90},
    {columnId: 'event',        fixed: true,  resizable: true,  defaultWidth: 260},
    {columnId: 'companyId',    fixed: false, resizable: true,  defaultWidth: 160},
    {columnId: 'year',         fixed: false, resizable: true,  defaultWidth: 80},
    {columnId: 'type',         fixed: false, resizable: true,  defaultWidth: 170},
    {columnId: 'status',       fixed: false, resizable: true,  defaultWidth: 130},
    {columnId: 'beginDate',    fixed: false, resizable: true,  defaultWidth: 140},
    {columnId: 'endDate',      fixed: false, resizable: true,  defaultWidth: 140},
    {columnId: 'time',         fixed: false, resizable: true,  defaultWidth: 100},
    {columnId: 'priority',     fixed: false, resizable: true,  defaultWidth: 90},
    {columnId: 'modifiedBy',   fixed: false, resizable: true,  defaultWidth: 160},
    {columnId: 'modifiedDate', fixed: false, resizable: true,  defaultWidth: 160},
    {columnId: 'edit',         fixed: false, resizable: false, defaultWidth: 90},
];

/** Human-readable header labels keyed by columnId */
const COLUMN_LABELS: Record<string, string> = {
    select:       '',
    eventId:      'Event ID',
    event:        'Event',
    companyId:    'Company ID',
    year:         'Year',
    type:         'Type',
    status:       'Status',
    beginDate:    'Begin Date',
    endDate:      'End Date',
    time:         'Time',
    priority:     'Priority',
    modifiedBy:   'Modified By',
    modifiedDate: 'Modified Date',
    edit:         'Edit Event',
};

/** Map EventData array to DataTable RowData format */
const mapEventsToRows = (events: EventData[]): RowData[] =>
    events.map(event => ({
        rowHeight: 36,
        id: event.eventId,
        columnValues: {
            eventId:      event.eventId,
            event:        event.event,
            companyId:    event.companyId,
            year:         event.eventYear,
            type:         event.eventType,
            status:       event.statusName,
            beginDate:    event.beginingDate,
            endDate:      event.endDate,
            time:         event.time,
            priority:     event.processPriority,
            modifiedBy:   event.modifiedBy,
            modifiedDate: event.modificationDate,
                edit:         '',
        },
    }));

/**
 * ManageEventCalendarView Component
 */
const ManageEventCalendarView: React.FC = () => {
    const {
        events, totalCount, currentPage, pageSize, loading, error,
        fetchDefaultEvents, searchWithFilters, handlePageChange,
        selectedEventIds, toggleStatusLoading, toggleFeedback,
        toggleSelectEvent, toggleSelectAll, clearSelection, clearToggleFeedback, toggleStatus,
    } = useEvents();

    // State for the status popup
    const [popupEventId, setPopupEventId] = useState<number | null>(null);

    /** Info/result modal shown after API response or for validation messages */
    const [infoModal, setInfoModal] = useState<ToggleFeedback | null>(null);

    const handleStatusClick = useCallback((eventId: number) => {
        setPopupEventId(eventId);
    }, []);

    const handlePopupClose = useCallback(() => {
        setPopupEventId(null);
    }, []);

    // Track if a search was just performed (to show alert on no results)
    const isSearchingRef = useRef(false);

    // Ref for the select-all checkbox (needed to set indeterminate state)
    const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

    // All event IDs in the current page / result set
    const allEventIds = useMemo(() => events.map(e => e.eventId), [events]);

    // Sync indeterminate state on the select-all checkbox
    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            const allSelected = allEventIds.length > 0 && allEventIds.every(id => selectedEventIds.includes(id));
            selectAllCheckboxRef.current.indeterminate = selectedEventIds.length > 0 && !allSelected;
        }
    }, [selectedEventIds, allEventIds]);

    // Fetch events on mount
    useEffect(() => {
        console.log('Mounting ManageEventCalendarView, fetching default events...');
        fetchDefaultEvents();
    }, [fetchDefaultEvents]);

    // Check for no results from search and show alert
    useEffect(() => {
        if (!loading && isSearchingRef.current && totalCount === 0) {
            // eslint-disable-next-line no-alert
            alert('No Event Information found');
            isSearchingRef.current = false;
        }
    }, [loading, totalCount]);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Handle search filters
    const handleSearch = useCallback((filters: SearchFiltersState) => {
        console.log('Search requested with filters:', filters);
        isSearchingRef.current = true;
        searchWithFilters(filters);
    }, [searchWithFilters]);

    // Lift toggleFeedback (from hook) into the local result modal
    useEffect(() => {
        if (toggleFeedback) {
            setInfoModal(toggleFeedback);
        }
    }, [toggleFeedback]);

    /**
     * Clicking Active/Inactive fires the API immediately (no confirmation step).
     * Validation (empty selection) is handled inside toggleStatus in the hook.
     */
    const handleToggleStatus = useCallback((targetStatus: 'ACTIVE' | 'INACTIVE') => {
        console.log('[handleToggleStatus] clicked:', targetStatus, '| selectedIds:', selectedEventIds);
        // toggleStatus validates empty selection and shows feedback if needed
        toggleStatus(targetStatus, selectedEventIds).then(() => {
            clearSelection();
        });
    }, [selectedEventIds, toggleStatus, clearSelection]);

    /** Dismiss the result/info modal and clear feedback state */
    const handleInfoModalClose = useCallback(() => {
        setInfoModal(null);
        clearToggleFeedback();
    }, [clearToggleFeedback]);

    // Handle pagination change
    const onPaginationChange: PaginationChangeHandler = useCallback(
        ({currentPage: page, pageSize: size}) => {
            console.log('Pagination changed:', {page, size});
            handlePageChange({currentPage: page, pageSize: size});
        },
        [handlePageChange]
    );

        const tableData = useMemo(
            () => ({columns: TABLE_COLUMNS, rows: mapEventsToRows(events)}),
            [events]
        );

    // --- Full-width / full-height layout via ResizeObserver ---
    // tableWrapperRef: plain ref for the ResizeObserver (no re-render needed).
    // tableWrapperEl: state copy so the DataTable can receive wrapperElement
    //   after the wrapper div is committed to the DOM (required before mount).
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const dataPanelRef = useRef<HTMLDivElement>(null);
    const [tableWrapperEl, setTableWrapperEl] = useState<HTMLDivElement | null>(null);

    // Callback ref: populates both the plain ref and the state.
    const tableWrapperCallback = useCallback((el: HTMLDivElement | null) => {
        tableWrapperRef.current = el;
        setTableWrapperEl(el);
    }, []);

    // Start with wide/tall defaults so there is no scroll-flash before the
    // ResizeObserver fires on the first paint.
    const [wrapperWidth, setWrapperWidth] = useState(window.innerWidth);
    const [dataPanelHeight, setDataPanelHeight] = useState(window.innerHeight);

    // Single ResizeObserver on the tableWrapper measures both dimensions.
    useLayoutEffect(() => {
        const el = tableWrapperRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => {
            const {width, height} = entries[0].contentRect;
            setWrapperWidth(width);
            setWrapperHeight(height);
        });
        observer.observe(el);
        return () => observer.disconnect();
    // Re-attach when the wrapper mounts (i.e. when tableWrapperEl changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableWrapperEl]);

    // Keep a separate wrapperHeight for the viewportHeight calculation.
    const [wrapperHeight, setWrapperHeight] = useState(window.innerHeight);

    // dataPanel ResizeObserver drives viewportHeight (via dataPanelHeight).
    useLayoutEffect(() => {
        const el = dataPanelRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => {
            setDataPanelHeight(entries[0].contentRect.height);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // --- Proportional column distribution ---
    // Fixed columns (select, eventId, edit) stay at their defined widths.
    // All other (resizable) columns are scaled proportionally so the grid
    // fills exactly the available wrapper width on every screen size.
    //   scale > 1 → columns grow (wide screen)
    //   scale < 1 → columns shrink (narrow screen), floored at 40% of base
    const FIXED_COL_IDS = useMemo(() => ['select', 'eventId', 'edit'], []);
    const FIXED_COL_TOTAL = useMemo(
        () => TABLE_COLUMNS
            .filter(c => FIXED_COL_IDS.includes(c.columnId as string))
            .reduce((s, c) => s + (c.defaultWidth ?? 80), 0),
        [FIXED_COL_IDS]
    );
    const RESIZABLE_BASE_TOTAL = useMemo(
        () => TABLE_COLUMNS
            .filter(c => !FIXED_COL_IDS.includes(c.columnId as string))
            .reduce((s, c) => s + (c.defaultWidth ?? 120), 0),
        [FIXED_COL_IDS]
    );

    const displayColumns = useMemo(() => {
        if (wrapperWidth <= 0) return TABLE_COLUMNS;
        const available = Math.max(1, wrapperWidth - FIXED_COL_TOTAL);
        const scale = available / RESIZABLE_BASE_TOTAL;
        return TABLE_COLUMNS.map(c => {
            if (FIXED_COL_IDS.includes(c.columnId as string)) return c;
            const base = c.defaultWidth ?? 120;
            // minimum = 65% of base so content (company IDs, statuses, dates) is always legible
            const min = Math.max(70, Math.round(base * 0.65));
            return {...c, defaultWidth: Math.max(min, Math.round(base * scale))};
        });
    }, [wrapperWidth, FIXED_COL_IDS, FIXED_COL_TOTAL, RESIZABLE_BASE_TOTAL]);

    const displayTableData = useMemo(
        () => ({columns: displayColumns, rows: mapEventsToRows(events)}),
        [displayColumns, events]
    );

    // Compute total columns width — used as minWidth on the DataTable so it
    // never shrinks below the sum of its column widths.
    const totalColumnsWidth = useMemo(() => displayColumns.reduce((sum, c) => sum + (c.defaultWidth ?? 120), 0), [displayColumns]);

    // DataTable header height: 3.333rem × 12px (PB kit base font) = 40px.
    // Navigation (pagination stripe) is position:sticky inside the scroll
    // viewport, so it does NOT add to the DataTable's external height.
    // viewportHeight = available data panel height − header height.
    const DT_HEADER_HEIGHT = 40;

    // Headers renderer function
    const renderHeaders: RenderHeaders = useCallback(
        ({column}, cellOptions: CellOptions) => {
            if (column.columnId === 'select') {
                const allSelected =
                    allEventIds.length > 0 && allEventIds.every(id => selectedEventIds.includes(id));
                return (
                    <Cell {...cellOptions} column={column}>
                        <div className={checkboxCell}>
                            <input
                                type="checkbox"
                                ref={selectAllCheckboxRef}
                                checked={allSelected}
                                onChange={() => toggleSelectAll(allEventIds)}
                                aria-label="Select all events"
                            />
                        </div>
                    </Cell>
                );
            }
            return (
                <Cell {...cellOptions} column={column} resizable={false}>
                    <TableHeader indent={Indent.NONE}>
                        <span style={{display:'flex', width:'100%', justifyContent:'flex-start'}}>{COLUMN_LABELS[column.columnId] ?? column.columnId}</span>
                    </TableHeader>
                </Cell>
            );
        },
        [allEventIds, selectedEventIds, toggleSelectAll]
    );

    // Rows renderer function
    const renderTableRows: RenderRows = useCallback(
        (rowsParams, cellOptions: CellOptions) => {
            const colId = rowsParams.column.columnId;
            if (colId === 'select') {
                return (
                    <Cell {...cellOptions} key={colId}>
                        <div className={checkboxCell}>
                            <input
                                type="checkbox"
                                checked={selectedEventIds.includes(rowsParams.row.id as number)}
                                onChange={() => toggleSelectEvent(rowsParams.row.id as number)}
                                aria-label={`Select event ${rowsParams.row.id}`}
                            />
                        </div>
                    </Cell>
                );
            }
            if (colId === 'edit') {
                return (
                    <Cell {...cellOptions} key={colId}>
                        <div className={cellContent}>
                        <a
                            href="#"
                            onClick={e => {
                                e.preventDefault();
                                (async () => {
                                    // Declare outside try so it is accessible after the block
                                    let fetchedEvent: EventData | undefined;
                                    try {
                                        fetchedEvent = await getEventById(String(rowsParams.row.id));
                                        dispatch(resetEventForm());
                                        dispatch(updateEventField({field: 'companyId', value: fetchedEvent.companyId ?? ''}));
                                        dispatch(updateEventField({field: 'companyName', value: fetchedEvent.companyName ?? ''}));
                                        dispatch(updateEventField({field: 'eventName', value: fetchedEvent.event ?? ''}));
                                        dispatch(updateEventField({field: 'eventType', value: String(fetchedEvent.eventType ?? '')}));
                                        dispatch(updateEventField({field: 'effectiveBeginDate', value: fetchedEvent.beginingDate ?? ''}));
                                        dispatch(updateEventField({field: 'effectiveBeginTime', value: fetchedEvent.time ?? ''}));
                                        dispatch(updateEventField({field: 'effectiveEndDate', value: fetchedEvent.endDate ?? ''}));
                                        dispatch(updateEventField({field: 'eventDetailUrl', value: fetchedEvent.eventDetailUrl ?? ''}));
                                        dispatch(updateEventField({field: 'webcastUrl', value: fetchedEvent.webcastUrl ?? ''}));
                                        dispatch(updateEventField({field: 'fiscalYearEnd', value: fetchedEvent.fiscalYearEndDate ?? ''}));
                                        dispatch(updateEventField({field: 'eventYear', value: fetchedEvent.eventYear ?? ''}));
                                        dispatch(updateEventField({field: 'status', value: fetchedEvent.statusName ?? ''}));
                                    } catch (err) {
                                        // continue to navigate even on error
                                    }
                                    // Use the fetched eventId when available, fall back to row id
                                    const idToPass = String(fetchedEvent?.eventId ?? rowsParams.row.id);
                                    navigate('/event-details', {state: {eventId: idToPass}});
                                })();
                            }}
                        >
                            Edit
                        </a>
                        </div>
                    </Cell>
                );
            }
            if (colId === 'status') {
                const statusValue = String(rowsParams.row.columnValues[colId] ?? '');
                return (
                    <Cell {...cellOptions} key={colId}>
                        <div className={cellContent} title={statusValue}>
                        <button
                            type="button"
                            className={statusLink}
                            onClick={() => handleStatusClick(rowsParams.row.id as number)}
                        >
                            {statusValue}
                        </button>
                        </div>
                    </Cell>
                );
            }
            const value = String(rowsParams.row.columnValues[colId] ?? '');
            return (
                <Cell {...cellOptions} key={colId}>
                    <div className={cellContent} title={value}>
                        {value}
                    </div>
                </Cell>
            );
        },
        [dispatch, navigate, handleStatusClick, selectedEventIds, toggleSelectEvent]
    );

    const handleAddNew = useCallback(async () => {
        // Reset the form (empty)
        dispatch(resetEventForm());

        // Prefetch event types and statuses so the event-details page has data ready
        const meta = {};

        try {
            dispatch(fetchEventTypesRequest({data: undefined, meta, error: false}));
            const etResp = await fetch(`${ROOT}/api/event-types`, {headers: {'Accept': 'application/json'}});
            if (etResp.ok) {
                const etData = await etResp.json();
                dispatch(fetchEventTypesSuccess({data: etData, meta, error: false}));
            } else {
                const err = await etResp.json().catch(() => ({code: etResp.status, message: `Failed to fetch event types (HTTP ${etResp.status})`}));
                dispatch(fetchEventTypesFailure({data: err, meta, error: true}));
            }
        } catch (err) {
            dispatch(fetchEventTypesFailure({data: {code: 0, message: err instanceof Error ? err.message : 'Network error'}, meta, error: true}));
        }

        try {
            dispatch(fetchStatusesRequest({data: undefined, meta, error: false}));
            const sResp = await fetch(`${ROOT}/api/statuses`, {headers: {'Accept': 'application/json'}});
            if (sResp.ok) {
                const sJson = await sResp.json();
                const sData = sJson.objectData ?? [];
                dispatch(fetchStatusesSuccess({data: sData, meta, error: false}));
            } else {
                const err = await sResp.json().catch(() => ({code: sResp.status, message: `Failed to fetch statuses (HTTP ${sResp.status})`}));
                dispatch(fetchStatusesFailure({data: err, meta, error: true}));
            }
        } catch (err) {
            dispatch(fetchStatusesFailure({data: {code: 0, message: err instanceof Error ? err.message : 'Network error'}, meta, error: true}));
        }

        // Navigate to event details page (create mode) without passing any eventId
        navigate('/event-details', {state: {}});
    }, [dispatch, navigate]);

    return (
        <div className={eventCalendarContainer}>
            {/* Status Popup */}
            {popupEventId !== null && (
                <StatusPopup eventId={popupEventId} onClose={handlePopupClose} />
            )}


            {/* Page-level overlay during toggle status API call — portalled to body to avoid flex/transform interference */}
            {toggleStatusLoading && createPortal(
                <div className={toggleOverlay}>
                    <div className={toggleSpinner} />
                    <span className={toggleOverlayText}>Updating status…</span>
                </div>,
                document.body
            )}

            {/* Result / Validation Info Modal */}
            {infoModal !== null && (
                <div className={modalOverlay} role="dialog" aria-modal="true" aria-labelledby="info-modal-title">
                    <div className={modalContainer}>
                        <div className={modalHeader}>
                            <span id="info-modal-title" className={modalTitle}>
                                {infoModal.type === 'success' ? 'Success'
                                    : infoModal.type === 'partial' ? 'Partial Success'
                                    : 'Notice'}
                            </span>
                        </div>
                        <div className={modalBody}>
                            <p>
                                {/* Render success part in green, remainder in default color */}
                                {infoModal.successText ? (
                                    <>
                                        <span className={modalTextSuccess}>{infoModal.successText}</span>
                                        {infoModal.remainderText && (
                                            <span>{infoModal.remainderText}</span>
                                        )}
                                    </>
                                ) : (
                                    <span className={
                                        infoModal.type === 'error' ? modalTextError : undefined
                                    }>
                                        {infoModal.text}
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className={modalFooter}>
                            <button
                                type="button"
                                className={modalButtonConfirm}
                                onClick={handleInfoModalClose}
                                autoFocus
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Section with Title */}
            <div className={headerSection}>
                <h2 className={headerTitle}>Manage Event Calendar</h2>
            </div>

            {/* Filters Panel - Compact */}
            <div className={filtersPanel}>
                <SearchFilters
                    onSearch={handleSearch}
                    onAddNew={handleAddNew}
                    loading={loading}
                    onToggleActive={() => handleToggleStatus('ACTIVE')}
                    onToggleInactive={() => handleToggleStatus('INACTIVE')}
                    toggleStatusLoading={toggleStatusLoading}
                />
            </div>

            {/* Data Panel - Takes remaining space */}
            <div className={dataPanel} ref={dataPanelRef}>
                {/* Loading State */}
                {loading && (
                    <div className={loadingContainer}>
                        <Loading />
                        <p>Loading events...</p>
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className={errorContainer}>
                        <h3>Error</h3>
                        <p>{error}</p>
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && events.length === 0 && (
                    <div className={emptyContainer}>
                        <h3>No Events Found</h3>
                        <p>There are currently no events to display.</p>
                    </div>
                )}

                {/* Data Table with Events */}
                {!loading && !error && events.length > 0 && (
                    <div className={tableContainer} data-testid="event-calendar-table">
                        <div className={tableWrapper} ref={tableWrapperCallback}>
                            {/* Delay DataTable render until the wrapper div is in the DOM
                                so wrapperElement is never null when componentDidMount runs. */}
                            {tableWrapperEl && (
                            <DataTable
                                style={{width: '100%', minWidth: totalColumnsWidth}}
                                data={displayTableData}
                                wrapperElement={tableWrapperEl}
                                containerEventListenersType="mainHeaderContainer:resize"
                                viewportHeight={Math.max(300, dataPanelHeight - DT_HEADER_HEIGHT)}
                                tableSize="DEFAULT"
                                rowsVirtualization={true}
                                minRowsForVirtualization={1}
                                minCellsForVirtualization={1}
                                overscanCellsRatio={1.05}
                                overscanRows={5}
                                reordering={{disabled: true, onChange: () => {}}}
                                headers={renderHeaders}
                                renderRows={renderTableRows}
                                paginationStripe={
                                    <PaginationStripe
                                        data-testid="event-calendar-pagination"
                                        totalCount={totalCount}
                                        pageSize={pageSize}
                                        currentPage={currentPage}
                                        sizes={PAGINATION_SIZES}
                                        onPaginationChange={onPaginationChange}
                                        sizeSwitcherPosition="LEFT"
                                    />
                                }
                            />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageEventCalendarView;
